import React, { useState, useEffect } from "react";
import { Edit, ArrowLeft, Upload, AlertCircle, ChevronRight, FileText, Eye, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { db, storage } from "../../firebase";
import { ref as dbRef, onValue, update } from "firebase/database";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { toast } from "react-toastify";

export default function Document() {
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [userInfo, setUserInfo] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [lotteryData, setLotteryData] = useState({});

  const documents = [
    { id: 1, name: "शासनमान्य संस्थेचे प्रमाणपत्र", englishName: "Accredited Certificate" },
    { id: 2, name: " शासनमान्य संस्थेचे शेवटच्या वर्षाचे गुणपत्रक.", englishName: "Statutory Institute Marksheet" },
    { id: 3, name: "अधिवस प्रमाणपत्र", englishName: "Domicile Certificate" },
    { id: 4, name: "नाव पत्ता असलेल्या कागदावर स्वतःचा फोटो लावून फोटो स्वसाक्षांकित करावा", englishName: "Self Attested Photo Document" },
    { id: 5, name: "आय कार्ड साईझ फोटो", englishName: "ID Card Size Photo" },
    { id: 6, name: "अर्जदाराची स्वाक्षरी", englishName: "Applicant Signature" },
    { id: 7, name: "आधार कार्ड", englishName: "Aadhar Card" },
    { id: 8, name: "पॅन कार्ड", englishName: "PAN Card" },
    { id: 9, name: "प्रतिज्ञा पत्र", englishName: "Affidavit" },
    { id: 10, name: "रोजगार कार्ड", englishName: "Employment Card" },
    { id: 11, name: "DSC टोकन", englishName: "DSC Token" },
    { id: 12, name: "Other Document", englishName: "Other Document" },
  ];

  const lotteryDocuments = [
    { id: 'aadharFile', name: "आधार कार्ड (लॉटरी)", englishName: "Aadhar Card (Lottery)" },
    { id: 'panFile', name: "पॅन कार्ड (लॉटरी)", englishName: "PAN Card (Lottery)" },
    { id: 'gstFile', name: "जी.एस.टी (लॉटरी)", englishName: "GST (Lottery)" },
    { id: 'regCertificateFile', name: "नोंदणी प्रमाणपत्र (लॉटरी)", englishName: "Registration Certificate (Lottery)" },
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('currentUser'));
    if (user) {
      setUserInfo(user);
      const docsRef = dbRef(db, `ekamwatapzp/EngineerRegistrations/${user._key}/documents`);
      const lotteryRef = dbRef(db, `ekamwatapzp/EngineerRegistrations/${user._key}/lotteryregistration`);
      
      const unsubscribe = onValue(docsRef, (snapshot) => {
        if (snapshot.exists()) {
          setUploadedFiles(snapshot.val());
        } else {
          setUploadedFiles({});
        }
        setLoading(false);
      });

      const unsubLottery = onValue(lotteryRef, (snapshot) => {
        if (snapshot.exists()) {
          setLotteryData(snapshot.val());
        } else {
          setLotteryData({});
        }
      });

      return () => {
        unsubscribe();
        unsubLottery();
      };
    } else {
      setLoading(false);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("फाईल 2MB पेक्षा लहान असावी.");
        return;
      }
      setCurrentFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const allStandardApproved = documents.every(doc => uploadedFiles[doc.id]?.approvedByIncharge === true);
  const displayDocs = allStandardApproved ? lotteryDocuments : documents;

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!currentFile || !userInfo || !selectedDoc) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const isLotteryDoc = typeof selectedDoc.id === 'string';
      const fileExt = currentFile.name.split('.').pop();
      const fileName = `${selectedDoc.id}_${Date.now()}.${fileExt}`;
      const folder = isLotteryDoc ? 'lottery' : 'documents';
      const fileRef = storageRef(storage, `ekamwatapzp/${folder}/${userInfo._key}/${fileName}`);
      
      const uploadTask = uploadBytesResumable(fileRef, currentFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(Math.round(progress));
        },
        (error) => {
          toast.error("अपलोड अयशस्वी झाले.");
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          if (isLotteryDoc) {
            const lotteryRef = dbRef(db, `ekamwatapzp/EngineerRegistrations/${userInfo._key}/lotteryregistration`);
            const updateData = {
              [selectedDoc.id]: downloadURL,
              [`${selectedDoc.id}Status`]: {
                name: currentFile.name,
                type: currentFile.type,
                uploadedAt: new Date().toISOString(),
                storagePath: `ekamwatapzp/${folder}/${userInfo._key}/${fileName}`
              }
            };
            await update(lotteryRef, updateData);
          } else {
            const userDocsRef = dbRef(db, `ekamwatapzp/EngineerRegistrations/${userInfo._key}/documents`);
            const newDocData = {
              [selectedDoc.id]: {
                url: downloadURL,
                storagePath: `ekamwatapzp/${folder}/${userInfo._key}/${fileName}`,
                name: currentFile.name,
                type: currentFile.type,
                uploadedAt: new Date().toISOString()
              }
            };
            await update(userDocsRef, newDocData);
          }

          toast.success(`${selectedDoc.name} यशस्वीरीत्या अपलोड झाले!`);
          setSelectedDoc(null);
          setCurrentFile(null);
          setPreviewUrl(null);
          setUploading(false);
        }
      );
    } catch (error) {
      toast.error("काहीतरी चूक झाली.");
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="animate-spin text-blue-600 mb-4" size={48} />
        <p className="text-slate-600 font-bold uppercase text-xs tracking-widest">Loading Documents...</p>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Header & Breadcrumb */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">कागदपत्रे पहा</h1>
          <p className="text-sm text-slate-500 mt-1">
            {allStandardApproved ? 'लॉटरी रजिस्ट्रेशन कागदपत्रे तपासा' : 'आपली वैयक्तिक कागदपत्रे तपासा आणि व्यवस्थापित करा'}
          </p>
        </div>
        <nav className="text-sm text-slate-500 bg-white px-4 py-2 rounded-full shadow-sm border flex items-center gap-2">
          <span>सांगली जिल्हा परिषद</span>
          <ChevronRight size={14} className="text-slate-400" />
          <span className="text-slate-700 font-medium font-inter cursor-pointer hover:text-blue-600" onClick={() => setSelectedDoc(null)}>कागदपत्रे पहा</span>
          {selectedDoc && (
            <>
              <ChevronRight size={14} className="text-slate-400" />
              <span className="text-blue-600 font-semibold">{selectedDoc.englishName}</span>
            </>
          )}
        </nav>
      </div>

      {!selectedDoc ? (
        /* Document List Table */
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="p-1 md:p-6">
            {allStandardApproved && (
              <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex items-center gap-3">
                <CheckCircle2 className="text-emerald-600" size={24} />
                <div>
                  <h4 className="font-bold text-emerald-900">सर्व वैयक्तिक कागदपत्रे मंजूर झाली आहेत!</h4>
                  <p className="text-xs text-emerald-700">आता तुम्ही लॉटरीसाठी आवश्यक कागदपत्रे पाहू शकता आणि अपलोड करू शकता.</p>
                </div>
              </div>
            )}
            <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm border-collapse bg-white">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-5 font-bold text-slate-700 text-sm w-16 text-center border-r border-slate-200">#</th>
                    <th className="px-6 py-5 text-left border-r border-slate-200 font-bold text-slate-700 text-sm italic">Document Category</th>
                    <th className="px-6 py-5 text-center border-r border-slate-200 font-bold text-slate-700 text-sm">Status</th>
                    <th className="px-6 py-5 text-center font-bold text-slate-700 text-sm w-40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayDocs.map((doc, index) => {
                    const uploaded = allStandardApproved ? lotteryData[doc.id] : uploadedFiles[doc.id];
                    const isApproved = !allStandardApproved && uploaded?.approvedByIncharge === true;

                    return (
                      <tr 
                        key={doc.id} 
                        className="border-b border-slate-100 hover:bg-slate-50/50 transition-all duration-200"
                      >
                        <td className="px-4 py-5 border-r border-slate-100 text-center font-bold text-slate-600">
                          {index + 1}
                        </td>
                        <td className="px-6 py-5 border-r border-slate-100">
                          <div className="flex flex-col">
                            <span className="font-bold text-slate-800 text-[15px]">{doc.name}</span>
                            <span className="text-xs text-slate-400 font-medium">{doc.englishName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-5 border-r border-slate-100 text-center">
                          {uploaded ? (
                            <div className="flex flex-col items-center gap-1">
                              <div className="flex items-center justify-center gap-2 text-emerald-600 font-bold text-sm bg-emerald-50 px-3 py-1 rounded-full border border-emerald-100 mx-auto w-fit">
                                <CheckCircle2 size={16} /> Uploaded
                              </div>
                              {isApproved && (
                                <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Approved</span>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center gap-2 text-rose-500 font-bold text-sm bg-rose-50 px-3 py-1 rounded-full border border-rose-100 mx-auto w-fit">
                              <XCircle size={16} /> Pending
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-5">
                          <div className="flex items-center justify-center gap-2">
                             <button 
                               onClick={() => setSelectedDoc(doc)}
                               className="flex items-center justify-center gap-2 py-2 px-3 rounded-xl font-bold transition-all shadow-sm border text-xs bg-white text-blue-600 border-blue-200 hover:bg-blue-50"
                             >
                               <Eye size={16} /> View/Upload
                             </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* Dynamic Document View/Upload */
        <div className="animate-in slide-in-from-right-8 duration-500">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8 max-w-4xl mx-auto flex flex-col items-start gap-8 min-h-[400px]">
            
            {/* Top Toolbar */}
            <div className="w-full flex items-center justify-between border-b border-slate-100 pb-6">
              <button 
                onClick={() => {
                  setSelectedDoc(null);
                  setPreviewUrl(null);
                  setCurrentFile(null);
                }}
                className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors font-medium bg-slate-50 px-4 py-2 rounded-lg"
              >
                <ArrowLeft size={18} /> BACK TO LIST
              </button>
              <div className="text-right">
                <span className="text-slate-400 text-xs font-bold uppercase block tracking-widest mb-1">Document Details</span>
                <h3 className="text-xl font-extrabold text-blue-900">{selectedDoc.englishName}</h3>
              </div>
            </div>

            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-12">
              <div className="flex flex-col gap-6">
                <div className="p-6 bg-blue-50/30 rounded-2xl border border-blue-100">
                  <h4 className="font-bold text-blue-900 mb-2">{selectedDoc.name}</h4>
                  <p className="text-sm text-blue-700 leading-relaxed">
                    कृपया या प्रवर्गासाठी आवश्यक कागदपत्र अपलोड करा. फाईलचा आकार 2MB पेक्षा कमी असावा.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-300">
                     <input 
                       type="file" 
                       accept="application/pdf,image/*" 
                       onChange={handleFileChange}
                       disabled={uploading}
                       className="block w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                     />
                  </div>

                  {uploading && (
                    <div className="space-y-2">
                       <div className="flex justify-between text-[10px] font-black text-blue-600 italic">
                          <span>UPLOADING...</span>
                          <span>{uploadProgress}%</span>
                       </div>
                       <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-600 transition-all duration-300" style={{width: `${uploadProgress}%`}}></div>
                       </div>
                    </div>
                  )}

                  <button 
                    onClick={handleUpload}
                    disabled={!currentFile || uploading}
                    className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold uppercase tracking-widest text-xs transition-all shadow-lg ${
                      !currentFile || uploading ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                    }`}
                  >
                     {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} 
                     {uploading ? 'UPLOADING...' : 'UPLOAD DOCUMENT'}
                  </button>
                </div>
              </div>

              <div className="bg-slate-900 rounded-2xl aspect-square flex flex-col items-center justify-center p-4 text-center border-4 border-slate-200 overflow-hidden shadow-2xl relative group">
                {previewUrl ? (
                   <div className="w-full h-full flex items-center justify-center animate-in zoom-in-95 duration-300">
                      {currentFile.type === 'application/pdf' ? (
                        <div className="flex flex-col items-center gap-4">
                          <FileText size={80} className="text-blue-500" />
                          <span className="text-white text-xs font-bold">{currentFile.name}</span>
                        </div>
                      ) : (
                        <img src={previewUrl} alt="Preview" className="max-h-full max-w-full rounded-lg object-contain" />
                      )}
                   </div>
                ) : (allStandardApproved ? lotteryData[selectedDoc.id] : uploadedFiles[selectedDoc.id]) ? (
                  <div className="w-full h-full flex flex-col items-center justify-center animate-in fade-in duration-500 p-4">
                     {(() => {
                        const file = allStandardApproved ? 
                          { url: lotteryData[selectedDoc.id], type: 'application/pdf', ...lotteryData[`${selectedDoc.id}Status`] } : 
                          uploadedFiles[selectedDoc.id];
                        
                        return (
                          <>
                            {file.type === 'application/pdf' ? (
                              <FileText size={80} className="text-emerald-500" />
                            ) : (
                              <img src={file.url} alt="Uploaded" className="max-h-full max-w-full rounded-lg object-contain" />
                            )}
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-xl">
                               <a 
                                 href={file.url} 
                                 target="_blank" 
                                 rel="noopener noreferrer"
                                 className="bg-white text-slate-900 px-4 py-2 rounded-lg font-bold text-xs flex items-center gap-2"
                               >
                                 <Eye size={16} /> VIEW FULL FILE
                               </a>
                            </div>
                          </>
                        );
                     })()}
                     <span className="mt-4 text-emerald-500 font-bold text-[10px] uppercase tracking-widest">Successfully Uploaded</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <FileText size={64} className="text-slate-700 mb-4 mx-auto" />
                    <p className="text-slate-500 text-xs tracking-widest uppercase font-bold">Preview Area</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}